import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { usePlayerLists, PlayerList, PlayerListItem } from '../hooks/usePlayerLists';

interface PlayerListContextType {
  lists: PlayerList[];
  defaultWishlist: PlayerList | null;
  isLoading: boolean;
  error: string | null;
  fetchLists: () => Promise<void>;
  getDefaultWishlist: () => Promise<PlayerList | null>;
  addPlayerToWishlist: (playerId: string) => Promise<PlayerList | null>;
  reorderWishlistItem: (currentIndex: number, newIndex: number) => Promise<PlayerList | null>;
  isPlayerInWishlist: (playerId: string) => boolean;
}

const PlayerListContext = createContext<PlayerListContextType | undefined>(undefined);

// Cache key for local storage
const WISHLIST_CACHE_KEY = 'cached_wishlist';
const CACHE_EXPIRY_KEY = 'cached_wishlist_expiry';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const PlayerListProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const playerListsHook = usePlayerLists();
  const [hasCachedData, setHasCachedData] = useState(false);
  
  // Try to load cached data on initial render
  useEffect(() => {
    const loadCachedData = () => {
      try {
        const expiryStr = localStorage.getItem(CACHE_EXPIRY_KEY);
        if (!expiryStr) return false;
        
        const expiry = parseInt(expiryStr, 10);
        const now = Date.now();
        
        // Check if cache is still valid
        if (now > expiry) {
          // Cache expired, clear it
          localStorage.removeItem(WISHLIST_CACHE_KEY);
          localStorage.removeItem(CACHE_EXPIRY_KEY);
          return false;
        }
        
        // Cache is valid, try to load it
        const cachedWishlistStr = localStorage.getItem(WISHLIST_CACHE_KEY);
        if (!cachedWishlistStr) return false;
        
        try {
          const cachedWishlist = JSON.parse(cachedWishlistStr) as PlayerList;
          
          // Only use cache if we don't already have data and the cache is valid
          if (!playerListsHook.defaultWishlist && cachedWishlist) {
            console.log('Using cached wishlist data');
            playerListsHook.lists = [cachedWishlist];
            playerListsHook.defaultWishlist = cachedWishlist;
            return true;
          }
        } catch (e) {
          console.error('Error parsing cached wishlist:', e);
          return false;
        }
      } catch (e) {
        console.error('Error loading cached wishlist:', e);
        return false;
      }
      
      return false;
    };
    
    setHasCachedData(loadCachedData());
  }, []);
  
  // Cache wishlist data whenever it changes
  useEffect(() => {
    if (playerListsHook.defaultWishlist) {
      try {
        localStorage.setItem(WISHLIST_CACHE_KEY, JSON.stringify(playerListsHook.defaultWishlist));
        localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
      } catch (e) {
        console.error('Error caching wishlist:', e);
      }
    }
  }, [playerListsHook.defaultWishlist]);
  
  return (
    <PlayerListContext.Provider value={{
      ...playerListsHook,
      // Override isLoading to account for cached data
      isLoading: hasCachedData ? false : playerListsHook.isLoading
    }}>
      {children}
    </PlayerListContext.Provider>
  );
};

export const usePlayerListContext = () => {
  const context = useContext(PlayerListContext);
  if (context === undefined) {
    throw new Error('usePlayerListContext must be used within a PlayerListProvider');
  }
  return context;
};